import { ISize } from 'shared/types/Size';
import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { getFirstLowestPriceVariants } from 'shared/utils/getFirstLowestPriceVariants';
import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';

/*
 * Sets map values to map sizes to map of variant types to lowest priced variant
 * For example: Map(1) {
 *  key: 7,
 *  value: Map(3) {
 *    0: {"bestPrice" => Variant}
 *    1: {"instantShip" => Variant}
 *    2: {"newWithBoxIssues" => Variant}
 *  }
 * }
 */
const createSizeToVariantMap = (
  map: Map<number, Map<VARIANT_KEYS, IBuyBarProductVariant>>,
  variants: IBuyBarProductVariant[],
  key: VARIANT_KEYS,
) => {
  for (const variant of variants) {
    if (!variant?.sizeOption?.value && variant?.sizeOption?.value !== 0) return;

    if (map.has(variant.sizeOption.value)) {
      map.get(variant.sizeOption.value).set(key, variant);
    } else {
      const variantMap = new Map();
      variantMap.set(key, variant);
      map.set(variant.sizeOption.value, variantMap);
    }
  }
};

const setSizePrice = (
  sizeMap: Map<VARIANT_KEYS, IBuyBarProductVariant>,
  priceMap: Map<number, IBuyBarProductVariant>,
  key: VARIANT_KEYS,
) => {
  if (sizeMap.has(key)) {
    const variant = sizeMap.get(key);
    const {
      sizeOption: { value: size },
    } = variant;

    if (priceMap.has(size)) return;

    priceMap.set(size, variant);
  }
};

const sortBySize = (a: IBuyBarProductVariant, b: IBuyBarProductVariant) => {
  if (a.sizeOption?.value < b.sizeOption?.value) {
    return -1;
  }
  if (a.sizeOption?.value > b.sizeOption?.value) {
    return 1;
  }
  return 0;
};

const getDisplayPrices = (variantMap: Map<number, Map<VARIANT_KEYS, IBuyBarProductVariant>>) => {
  const sizeToPriceMap = new Map<number, IBuyBarProductVariant>();

  variantMap.forEach((size) => {
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.BEST_PRICE);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.NEW_WITH_BOX_ISSUES);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.NEW_WITH_DEFECTS);
    setSizePrice(size, sizeToPriceMap, VARIANT_KEYS.USED);
  });
  const sortedSizeToPriceMapValues = [...sizeToPriceMap.values()].sort(sortBySize);

  return sortedSizeToPriceMapValues;
};

export const getAvailableSizesPrioritizedByCondition = (
  productVariants: IBuyBarProductVariant[],
  getInstantShipLowestPrice = false,
  productTemplateSizeOptionsMap: Map<number, ISize> = null,
) => {
  // #TODO: Refactor: Combine all productVariants forLoop into one.
  const bestPriceVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [BOX_CONDITIONS.GOOD_CONDITION],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const instantShipVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [],
    true,
    productTemplateSizeOptionsMap,
  );

  const newWithBoxIssuesVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_NO_DEFECTS],
    [BOX_CONDITIONS.NO_ORIGINAL_BOX, BOX_CONDITIONS.BADLY_DAMAGED, BOX_CONDITIONS.MISSING_LID],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const newWithDefectsVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.NEW_WITH_DEFECTS],
    [],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const usedVariant = getFirstLowestPriceVariants(
    productVariants,
    [SHOE_CONDITIONS.USED, SHOE_CONDITIONS.GOAT_CLEAN],
    [],
    getInstantShipLowestPrice,
    productTemplateSizeOptionsMap,
  );

  const mapSizeToVariants = new Map<number, Map<VARIANT_KEYS, IBuyBarProductVariant>>();

  createSizeToVariantMap(mapSizeToVariants, bestPriceVariant, VARIANT_KEYS.BEST_PRICE);
  createSizeToVariantMap(mapSizeToVariants, instantShipVariant, VARIANT_KEYS.INSTANT_SHIP);
  createSizeToVariantMap(
    mapSizeToVariants,
    newWithBoxIssuesVariant,
    VARIANT_KEYS.NEW_WITH_BOX_ISSUES,
  );
  createSizeToVariantMap(mapSizeToVariants, newWithDefectsVariant, VARIANT_KEYS.NEW_WITH_DEFECTS);
  createSizeToVariantMap(mapSizeToVariants, usedVariant, VARIANT_KEYS.USED);

  return { availableSizes: getDisplayPrices(mapSizeToVariants), mapSizeToVariants, usedVariant };
};
